import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { analyticsAPI, salesAPI, stockAPI } from '@/lib/api';
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Users, TrendingUp, TrendingDown, Minus, RefreshCw, Search,
  ChevronUp, ChevronDown, ChevronsUpDown, X, Phone, Star,
  ShoppingBag, IndianRupee, Package, CreditCard, BarChart2,
  UserCheck, UserPlus, Lock,
} from 'lucide-react';

// ── Tier config ───────────────────────────────────────────────────────────────
const TIERS = {
  champion: { label: 'Champion', emoji: '🏆', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200',
              desc: 'High spend + frequent visits — your best customers. Reward and retain them.' },
  loyal:    { label: 'Loyal',    emoji: '⭐', bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',
              desc: 'Buys regularly and spends at or above average. Solid, dependable customers.' },
  new:      { label: 'New',      emoji: '🆕', bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',
              desc: 'First purchase within the last 60 days. Focus on making a great impression.' },
  at_risk:  { label: 'At Risk',  emoji: '⚠️', bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',
              desc: 'Hasn\'t visited recently or hasn\'t purchased yet. Needs re-engagement.' },
};

const TIER_ORDER = ['champion', 'loyal', 'new', 'at_risk'];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt    = (n) => typeof n === 'number' ? n.toLocaleString('en-IN') : '—';
const fmtCur = (n) => typeof n === 'number'
  ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
  : '—';

const BlurredValue = ({ children }) => (
  <span style={{ filter: 'blur(5px)' }} className="select-none pointer-events-none">{children}</span>
);

const TrendIcon = ({ trend }) => {
  if (trend === 'up')   return <TrendingUp   className="w-3.5 h-3.5 text-emerald-500" />;
  if (trend === 'down') return <TrendingDown  className="w-3.5 h-3.5 text-red-400" />;
  return <Minus className="w-3.5 h-3.5 text-slate-400" />;
};

const TierBadge = ({ tier }) => {
  const t = TIERS[tier] || TIERS.loyal;
  return (
    <span className="relative group inline-flex">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border cursor-default ${t.bg} ${t.text} ${t.border}`}>
        <span>{t.emoji}</span>{t.label}
      </span>
      {/* Hover tooltip */}
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 rounded-lg bg-slate-800 text-white text-xs px-3 py-2 shadow-lg leading-snug opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 whitespace-normal text-center">
        <span className="block font-semibold mb-0.5">{t.emoji} {t.label}</span>
        {t.desc}
        {/* Arrow */}
        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
      </span>
    </span>
  );
};

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <ChevronsUpDown className="w-3 h-3 opacity-30 inline ml-1" />;
  return sortDir === 'asc'
    ? <ChevronUp   className="w-3 h-3 inline ml-1" />
    : <ChevronDown className="w-3 h-3 inline ml-1" />;
};

// ── Mini sale row in side panel (expandable items) ────────────────────────────
const SalePanelRow = ({ sale }) => {
  const [open, setOpen] = useState(false);
  const date = sale.created_at
    ? new Date(sale.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
  const isDimmed = sale.status === 'amended' || sale.status === 'deleted';

  return (
    <>
      <tr
        className={`text-xs border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${isDimmed ? 'opacity-50' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <td className="px-3 py-2 font-mono text-slate-500">{sale.bill_number || sale.id?.slice(-6)}</td>
        <td className="px-3 py-2 text-slate-500">{date}</td>
        <td className="px-3 py-2 capitalize text-slate-600">{sale.payment_mode}</td>
        <td className="px-3 py-2 text-right font-semibold text-slate-700">{fmtCur(sale.total_amount)}</td>
        <td className="px-3 py-2 text-center text-slate-400">{sale.items?.length ?? 0}</td>
      </tr>
      {open && sale.items?.map((item, idx) => (
        <tr key={idx} className="bg-slate-50 text-xs border-b border-slate-100">
          <td colSpan={2} className="px-3 py-1.5 pl-8 text-slate-500">↳ {item.product_name}</td>
          <td className="px-3 py-1.5 text-slate-400">{item.quantity} × {fmtCur(item.unit_price)}</td>
          <td className="px-3 py-1.5 text-right text-slate-600">
            {fmtCur(item.total_price ?? item.quantity * item.unit_price)}
          </td>
          <td />
        </tr>
      ))}
    </>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const CustomerInsights = ({ previewMode = false }) => {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [sortBy, setSortBy]         = useState('total_spent');
  const [sortDir, setSortDir]       = useState('desc');

  // Side panel
  const [panel, setPanel]               = useState(null);
  const [panelSales, setPanelSales]     = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);
  const [salesCache, setSalesCache]     = useState({});
  const [panelTab, setPanelTab]         = useState('profile');

  // Stock products for category map (reuses Stock page React Query cache)
  const { data: stockProducts = [] } = useQuery({
    queryKey: ['stock-products'],
    queryFn: () => stockAPI.getAll({ limit: 500 }).then(r => r.data?.items ?? r.data ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const catMap = useMemo(() => {
    const m = {};
    stockProducts.forEach(p => { if (p.catalog_id && p.category) m[p.catalog_id] = p.category; });
    return m;
  }, [stockProducts]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await analyticsAPI.getCustomerInsights();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load customer insights');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openPanel = async (customer) => {
    setPanel(customer);
    setPanelTab('profile');
    if (salesCache[customer.customer_id]) {
      setPanelSales(salesCache[customer.customer_id]);
      return;
    }
    setPanelLoading(true);
    try {
      const res = await salesAPI.getAll({ customer_id: customer.customer_id, limit: 500 });
      const items = res.data?.items ?? res.data ?? [];
      setPanelSales(items);
      setSalesCache(prev => ({ ...prev, [customer.customer_id]: items }));
    } catch {
      toast.error('Failed to load sales for this customer');
    } finally {
      setPanelLoading(false);
    }
  };

  const closePanel = () => { setPanel(null); setPanelSales([]); };

  const handleSort = (col) => {
    setSortDir(d => col === sortBy ? (d === 'asc' ? 'desc' : 'asc') : 'desc');
    setSortBy(col);
  };

  const customers = useMemo(() => {
    if (!data?.customers) return [];
    let arr = data.customers;
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.mobile?.includes(q) ||
        c.top_product?.toLowerCase().includes(q)
      );
    }
    if (tierFilter !== 'all') {
      arr = arr.filter(c => c.tier === tierFilter);
    }
    arr = [...arr].sort((a, b) => {
      let av = a[sortBy] ?? -1;
      let bv = b[sortBy] ?? -1;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
    return arr;
  }, [data, search, tierFilter, sortBy, sortDir]);

  const unlockedIds = useMemo(() => {
    if (!previewMode || !data?.customers) return null;
    const top3 = [...data.customers]
      .sort((a, b) => (b.total_spent ?? 0) - (a.total_spent ?? 0))
      .slice(0, 3)
      .map(c => c.customer_id);
    return new Set(top3);
  }, [previewMode, data]);

  const summary = data?.summary || {};

  const categorySpendData = useMemo(() => {
    const spend = {};
    panelSales.forEach(sale => {
      (sale.items || []).forEach(item => {
        const cat = catMap[item.product_id] || 'Other';
        spend[cat] = (spend[cat] || 0) + (item.total_price ?? item.item_total ?? item.quantity * item.unit_price ?? 0);
      });
    });
    return Object.entries(spend)
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value);
  }, [panelSales, catMap]);

  const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

  const SortTh = ({ col, label, center = false }) => (
    <th
      onClick={() => handleSort(col)}
      className={`px-4 py-3 font-medium text-xs uppercase tracking-wide text-muted-foreground cursor-pointer select-none hover:bg-slate-100 transition-colors whitespace-nowrap ${center ? 'text-center' : 'text-left'}`}
    >
      {label}<SortIcon col={col} sortBy={sortBy} sortDir={sortDir} />
    </th>
  );

  return (
    <div className="flex h-full min-h-screen">

      {/* ── Main content ── */}
      <div className="flex-1 p-6 lg:p-8 space-y-5 overflow-auto min-w-0">

        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart2 className="w-6 h-6 text-primary" /> Customer Insights
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Understand your customers — who's loyal, who's at risk, who drives your revenue
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="gap-1.5">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Summary cards — always fully visible */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Customers',   value: fmt(summary.total_customers),    icon: Users,        color: 'text-blue-600',   bg: 'bg-blue-50'   },
            { label: 'Active This Month', value: fmt(summary.active_this_month),  icon: UserCheck,    color: 'text-emerald-600',bg: 'bg-emerald-50'},
            { label: 'New Customers',     value: fmt(summary.new_customers),      icon: UserPlus,     color: 'text-green-600',  bg: 'bg-green-50'  },
            { label: 'Avg Order Value',   value: fmtCur(summary.avg_order_value), icon: IndianRupee,  color: 'text-purple-600', bg: 'bg-purple-50' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <Card key={label} className="p-4">
              <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-2`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <p className="text-xs text-muted-foreground leading-tight">{label}</p>
              <p className={`text-lg font-bold ${color} mt-0.5`}>{loading ? '…' : value}</p>
            </Card>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative max-w-xs flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search name, mobile, product…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {[{ key: 'all', label: 'All' }, ...TIER_ORDER.map(k => ({ key: k, label: TIERS[k].label }))].map(({ key, label }) => (
              <span key={key} className="relative group inline-flex">
                <button
                  onClick={() => setTierFilter(key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    tierFilter === key
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {key !== 'all' && <span className="mr-1">{TIERS[key].emoji}</span>}{label}
                  {key !== 'all' && data && (
                    <span className="ml-1 opacity-60">
                      ({data.customers.filter(c => c.tier === key).length})
                    </span>
                  )}
                </button>
                {/* Tier description tooltip — skip for "All" */}
                {key !== 'all' && (
                  <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 rounded-lg bg-slate-800 text-white text-xs px-3 py-2 shadow-lg leading-snug opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 whitespace-normal text-center">
                    <span className="block font-semibold mb-0.5">{TIERS[key].emoji} {TIERS[key].label}</span>
                    {TIERS[key].desc}
                    <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Preview mode upgrade banner — above the table, visible without scrolling */}
        {previewMode && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-violet-50 border border-violet-200 rounded-lg">
            <Lock className="w-4 h-4 text-violet-700 shrink-0" />
            <p className="text-sm text-violet-800 flex-1">
              Viewing <strong>top 3</strong> of <strong>{data?.customers?.length ?? 0}</strong> customers · rest locked
            </p>
            <Link
              to="/settings"
              className="text-xs font-bold text-violet-700 bg-violet-100 hover:bg-violet-200 px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors"
            >
              Upgrade to Enterprise →
            </Link>
          </div>
        )}

        {/* Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b">
                <tr>
                  <SortTh col="name"                     label="Customer" />
                  <SortTh col="tier_rank"                label="Tier" />
                  <SortTh col="total_spent"              label="Total Spent" />
                  <SortTh col="avg_order_value"          label="Avg Order" center />
                  <SortTh col="days_since_last_purchase" label="Last Visit" center />
                  <SortTh col="top_product"              label="Top Product" />
                  <SortTh col="preferred_payment_mode"   label="Payment" center />
                  <SortTh col="spending_trend"           label="Trend" center />
                  <SortTh col="loyalty_points"           label="Pts" center />
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-16 text-muted-foreground">Loading insights…</td></tr>
                ) : customers.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-16 text-muted-foreground">No customers match</td></tr>
                ) : customers.map((c) => {
                  const isActive = panel?.customer_id === c.customer_id;
                  const isLocked = previewMode && !unlockedIds?.has(c.customer_id);
                  return (
                    <tr
                      key={c.customer_id}
                      onClick={() => {
                        if (isLocked) return;
                        isActive ? closePanel() : openPanel(c);
                      }}
                      className={`transition-colors ${
                        isLocked
                          ? 'cursor-default'
                          : isActive
                            ? 'bg-primary/5 border-l-2 border-l-primary cursor-pointer'
                            : 'hover:bg-muted/30 cursor-pointer'
                      }`}
                    >
                      {/* Customer name + mobile — always visible */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                            c.tier === 'champion' ? 'bg-yellow-100 text-yellow-700' :
                            c.tier === 'at_risk'  ? 'bg-amber-100 text-amber-700'  :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {c.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-slate-800 leading-tight">{c.name}</p>
                            {c.mobile && (
                              <p className="text-xs text-muted-foreground">
                                {isLocked ? <BlurredValue>{c.mobile}</BlurredValue> : c.mobile}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Tier badge — always visible */}
                      <td className="px-4 py-3"><TierBadge tier={c.tier} /></td>

                      <td className="px-4 py-3 font-semibold text-slate-800">
                        {isLocked ? <BlurredValue>{fmtCur(c.total_spent)}</BlurredValue> : fmtCur(c.total_spent)}
                      </td>

                      <td className="px-4 py-3 text-center text-slate-600">
                        {isLocked ? <BlurredValue>{fmtCur(c.avg_order_value)}</BlurredValue> : fmtCur(c.avg_order_value)}
                      </td>

                      <td className="px-4 py-3 text-center">
                        {c.days_since_last_purchase != null ? (
                          isLocked ? (
                            <BlurredValue>
                              <span className="text-xs font-medium">
                                {c.days_since_last_purchase === 0 ? 'Today' :
                                 c.days_since_last_purchase === 1 ? '1 day ago' :
                                 `${c.days_since_last_purchase}d ago`}
                              </span>
                            </BlurredValue>
                          ) : (
                            <span className={`text-xs font-medium ${
                              c.days_since_last_purchase <= 7  ? 'text-emerald-600' :
                              c.days_since_last_purchase <= 30 ? 'text-slate-600'   :
                              c.days_since_last_purchase <= 60 ? 'text-amber-600'   : 'text-red-500'
                            }`}>
                              {c.days_since_last_purchase === 0 ? 'Today' :
                               c.days_since_last_purchase === 1 ? '1 day ago' :
                               `${c.days_since_last_purchase}d ago`}
                            </span>
                          )
                        ) : <span className="text-slate-300 text-xs">Never</span>}
                      </td>

                      <td className="px-4 py-3 max-w-[140px]">
                        {c.top_product ? (
                          isLocked
                            ? <BlurredValue><span className="text-xs text-slate-600">{c.top_product}</span></BlurredValue>
                            : <span className="text-xs text-slate-600 truncate block" title={c.top_product}>{c.top_product}</span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>

                      <td className="px-4 py-3 text-center">
                        {c.preferred_payment_mode ? (
                          isLocked
                            ? <BlurredValue><span className="text-xs capitalize px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{c.preferred_payment_mode}</span></BlurredValue>
                            : <span className="text-xs capitalize px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{c.preferred_payment_mode}</span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>

                      <td className="px-4 py-3">
                        {isLocked
                          ? <div className="flex justify-center"><BlurredValue><TrendIcon trend={c.spending_trend} /></BlurredValue></div>
                          : <div className="flex justify-center"><TrendIcon trend={c.spending_trend} /></div>
                        }
                      </td>

                      <td className="px-4 py-3 text-center">
                        {(c.loyalty_points ?? 0) > 0 ? (
                          isLocked
                            ? <BlurredValue>
                                <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-yellow-700">
                                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />{c.loyalty_points}
                                </span>
                              </BlurredValue>
                            : <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-yellow-700">
                                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />{c.loyalty_points}
                              </span>
                        ) : <span className="text-slate-300 text-xs">0</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && (
            <div className="px-4 py-2 border-t bg-muted/20 text-xs text-muted-foreground">
              Showing {customers.length} of {data?.customers?.length ?? 0} customers
            </div>
          )}
        </Card>
      </div>

      {/* ── Side Panel ── */}
      {panel && (
        <div className="w-[460px] shrink-0 border-l bg-background flex flex-col shadow-xl overflow-hidden sticky top-0 h-screen">

          {/* Panel header */}
          <div className="p-5 border-b bg-muted/30 shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold shrink-0 ${
                  panel.tier === 'champion' ? 'bg-yellow-100 text-yellow-700' :
                  panel.tier === 'at_risk'  ? 'bg-amber-100 text-amber-700'  :
                  panel.tier === 'new'      ? 'bg-green-100 text-green-700'  :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {panel.name?.[0]?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-bold text-lg leading-tight">{panel.name}</p>
                  {panel.mobile && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" />{panel.mobile}
                    </p>
                  )}
                  <div className="mt-1.5"><TierBadge tier={panel.tier} /></div>
                </div>
              </div>
              <button
                onClick={closePanel}
                className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground shrink-0 mt-0.5"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-2 mt-4">
              {[
                { label: 'Total Spent',  value: fmtCur(panel.total_spent),     icon: IndianRupee },
                { label: 'Transactions', value: fmt(panel.transaction_count),   icon: ShoppingBag },
                { label: 'Avg Order',    value: fmtCur(panel.avg_order_value),  icon: BarChart2   },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="bg-background rounded-lg p-2.5 text-center border">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="font-bold text-slate-800 text-sm mt-0.5">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b shrink-0">
            {[['profile', 'Profile'], ['sales', 'Sales History']].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setPanelTab(key)}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                  panelTab === key
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Panel body */}
          <div className="flex-1 overflow-y-auto">

            {/* ── Sales History tab ── */}
            {panelTab === 'sales' && (
              <>
                {panelLoading ? (
                  <p className="text-center py-10 text-muted-foreground text-sm">Loading sales…</p>
                ) : panelSales.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No sales recorded yet</p>
                  </div>
                ) : (
                  <>
                    {/* Category spend pie chart */}
                    {categorySpendData.length >= 2 && (
                      <div className="px-4 pt-4 pb-2 border-b border-slate-100">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Spend by Category</p>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={categorySpendData}
                              cx="50%"
                              cy="45%"
                              outerRadius={75}
                              dataKey="value"
                              labelLine={false}
                            >
                              {categorySpendData.map((_, i) => (
                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <RechartsTooltip
                              formatter={(value, name) => {
                                const total = categorySpendData.reduce((s, d) => s + d.value, 0);
                                const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                return [`₹${value.toLocaleString('en-IN')} (${pct}%)`, name];
                              }}
                              contentStyle={{ fontSize: 11, borderRadius: 8 }}
                            />
                            <Legend
                              iconType="circle"
                              iconSize={8}
                              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                              formatter={(value, entry) => {
                                const total = categorySpendData.reduce((s, d) => s + d.value, 0);
                                const pct = total > 0 ? ((entry.payload.value / total) * 100).toFixed(0) : 0;
                                return `${value} (${pct}%)`;
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40 sticky top-0 z-10">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Bill #</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Date</th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Mode</th>
                        <th className="px-3 py-2 text-right font-medium text-muted-foreground">Amount</th>
                        <th className="px-3 py-2 text-center font-medium text-muted-foreground">Items</th>
                      </tr>
                    </thead>
                    <tbody>
                      {panelSales.map(sale => (
                        <SalePanelRow key={sale.id} sale={sale} />
                      ))}
                    </tbody>
                    <tfoot className="bg-muted/20 border-t sticky bottom-0">
                      <tr>
                        <td colSpan={3} className="px-3 py-2 text-xs font-semibold text-slate-600">
                          {panelSales.length} bills total
                        </td>
                        <td className="px-3 py-2 text-right text-xs font-bold text-slate-800">
                          {fmtCur(panelSales.reduce((s, x) => s + (x.total_amount || 0), 0))}
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                  </>
                )}
              </>
            )}

            {/* ── Profile tab ── */}
            {panelTab === 'profile' && (
              <div className="p-5 space-y-5">

                {/* Behaviour */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Behaviour</p>
                  <div className="space-y-2">
                    {[
                      {
                        icon: Package,
                        label: 'Top Product',
                        value: panel.top_product || '—',
                        sub: panel.top_category ? `Category: ${panel.top_category}` : null,
                      },
                      {
                        icon: CreditCard,
                        label: 'Preferred Payment',
                        value: panel.preferred_payment_mode
                          ? panel.preferred_payment_mode.toUpperCase()
                          : '—',
                      },
                      {
                        icon: ShoppingBag,
                        label: 'Avg Basket Size',
                        value: panel.avg_basket_size > 0
                          ? `${panel.avg_basket_size} items per visit`
                          : '—',
                      },
                      {
                        icon: Users,
                        label: 'Visit Frequency',
                        value: panel.purchase_frequency_days
                          ? `Every ~${panel.purchase_frequency_days} days`
                          : panel.transaction_count === 1
                            ? 'Only 1 visit so far'
                            : '—',
                      },
                    ].map(({ icon: Icon, label, value, sub }) => (
                      <div key={label} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                        <div className="w-8 h-8 rounded-lg bg-background border flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">{label}</p>
                          <p className="font-semibold text-slate-800 text-sm">{value}</p>
                          {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Spending trend */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Spending (Last 30d vs previous 30d)</p>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                    <TrendIcon trend={panel.spending_trend} />
                    <div>
                      <p className="font-semibold text-slate-800">{fmtCur(panel.recent_30d_spend)} this month</p>
                      <p className="text-xs text-muted-foreground">
                        {panel.spending_trend === 'up'   ? 'Spending is increasing ↑' :
                         panel.spending_trend === 'down' ? 'Spending is declining ↓'  :
                         'Spending is stable →'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Loyalty & Khata */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg border text-center">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400 mx-auto mb-1" />
                    <p className="text-xl font-bold text-yellow-700">{panel.loyalty_points ?? 0}</p>
                    <p className="text-xs text-muted-foreground">Loyalty Points</p>
                    <p className="text-xs text-muted-foreground mt-0.5">1 pt per ₹100 spent</p>
                  </div>
                  <div className={`p-3 rounded-lg border text-center ${(panel.khata_balance ?? 0) > 0 ? 'border-amber-200 bg-amber-50' : ''}`}>
                    <IndianRupee className={`w-5 h-5 mx-auto mb-1 ${(panel.khata_balance ?? 0) > 0 ? 'text-amber-600' : 'text-emerald-500'}`} />
                    <p className={`text-xl font-bold ${(panel.khata_balance ?? 0) > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>
                      {fmtCur(panel.khata_balance ?? 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Khata Balance</p>
                    <p className="text-xs mt-0.5 font-medium">
                      {(panel.khata_balance ?? 0) > 0 ? '⚠ Outstanding' : '✓ Cleared'}
                    </p>
                  </div>
                </div>

                {/* Last visit status */}
                {panel.days_since_last_purchase != null && (
                  <div className={`p-3 rounded-lg border text-sm ${
                    panel.days_since_last_purchase > 60  ? 'border-red-200 bg-red-50 text-red-700' :
                    panel.days_since_last_purchase > 30  ? 'border-amber-200 bg-amber-50 text-amber-700' :
                    'border-emerald-200 bg-emerald-50 text-emerald-700'
                  }`}>
                    <p className="font-semibold">
                      {panel.days_since_last_purchase === 0 ? '🟢 Visited today' :
                       panel.days_since_last_purchase === 1 ? '🟢 Visited yesterday' :
                       panel.days_since_last_purchase <= 7  ? `🟢 Last visit ${panel.days_since_last_purchase} days ago` :
                       panel.days_since_last_purchase <= 30 ? `🟡 Last visit ${panel.days_since_last_purchase} days ago` :
                       `🔴 Last visit ${panel.days_since_last_purchase} days ago`}
                    </p>
                    {panel.tier === 'at_risk' && (
                      <p className="text-xs mt-0.5 opacity-80">This customer may need re-engagement</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerInsights;
